import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { useTranslation } from 'react-i18next';

/**
 * Şube + metin arama filtre çubuğu.
 *
 * Satış ekranı listeleri (hızlı satış, restoran, paket) ve entegrasyon listesi
 * onlarca satıra ulaştığı için tek tek göz taramak zorlaşıyordu. Bu bileşen
 * o listelerin hepsinde aynı filtre deneyimini verir.
 *
 * Filtreleme çağıran tarafta yapılır (bileşen sadece kontrollü girdi sunar);
 * böylece her sayfa kendi alanlarında (ekran adı, mağaza adı, platform...)
 * arama yapabilir.
 */

export interface BranchFilterOption {
  id: string;
  name: string;
}

/** Şubesiz kayıtları süzmek için ayrılmış sanal şube id'si. */
export const UNASSIGNED_BRANCH_ID = '__unassigned__';

interface BranchSearchFilterBarProps {
  branches: BranchFilterOption[];
  /** null = tüm şubeler. UNASSIGNED_BRANCH_ID = şubeye bağlı olmayan kayıtlar. */
  branchId: string | null;
  onBranchChange: (branchId: string | null) => void;
  search: string;
  onSearchChange: (value: string) => void;
  /** Arama alanı etiketi (ör. "Ekran Adı"). Boşsa genel "Ara" kullanılır. */
  searchLabel?: string;
  searchPlaceholder?: string;
  /** Filtre sonrası gösterilen kayıt sayısı — sayaç rozeti için. */
  shownCount?: number;
  /** Filtresiz toplam kayıt sayısı. */
  totalCount?: number;
  /** Şubesiz seçeneğini listeye ekler (şubesiz kayıt olabilen listeler için). */
  includeUnassignedOption?: boolean;
  /** Şubesiz seçeneğinin etiketi; listeye göre değişir (ör. "Şirket Geneli"). */
  unassignedLabel?: string;
  disabled?: boolean;
}

const BranchSearchFilterBar: React.FC<BranchSearchFilterBarProps> = ({
  branches,
  branchId,
  onBranchChange,
  search,
  onSearchChange,
  searchLabel,
  searchPlaceholder,
  shownCount,
  totalCount,
  includeUnassignedOption = true,
  unassignedLabel,
  disabled = false,
}) => {
  const { t } = useTranslation('common2');

  const options: BranchFilterOption[] = React.useMemo(() => {
    const list = [...branches];
    if (includeUnassignedOption) {
      list.push({
        id: UNASSIGNED_BRANCH_ID,
        name: unassignedLabel || t('listFilter.branchUnassigned'),
      });
    }
    return list;
  }, [branches, includeUnassignedOption, unassignedLabel, t]);

  const selectedBranch = options.find((o) => o.id === branchId) || null;
  const hasFilter = Boolean(branchId) || search.trim().length > 0;
  const showCount = typeof shownCount === 'number' && typeof totalCount === 'number';

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              size="small"
              fullWidth
              options={options}
              value={selectedBranch}
              onChange={(_e, value) => onBranchChange(value?.id ?? null)}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={disabled}
              noOptionsText={t('listFilter.noBranches')}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon fontSize="small" />
                    {option.name}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('listFilter.branchLabel')}
                  placeholder={t('listFilter.branchPlaceholder')}
                  size="small"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={5}>
            <TextField
              size="small"
              fullWidth
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              label={searchLabel || t('listFilter.searchLabel')}
              placeholder={searchPlaceholder || t('listFilter.searchPlaceholder')}
              disabled={disabled}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => onSearchChange('')}
                      aria-label={t('listFilter.clearSearch')}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
          >
            {showCount && (
              <Chip
                size="small"
                variant="outlined"
                label={
                  hasFilter
                    ? t('listFilter.resultCount', { shown: shownCount, total: totalCount })
                    : t('listFilter.totalCount', { total: totalCount })
                }
              />
            )}
            {hasFilter && (
              <Button
                size="small"
                color="inherit"
                startIcon={<FilterAltOffIcon />}
                onClick={() => {
                  onBranchChange(null);
                  onSearchChange('');
                }}
              >
                {t('listFilter.clear')}
              </Button>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default BranchSearchFilterBar;
